import React, { Component } from "react";
import PropTypes from "prop-types";
import ReactDOM from "react-dom";
import keycode from "keycode";
import Menu from "material-ui/Menu";
import MenuItem from "material-ui/MenuItem";
import Divider from "material-ui/Divider";
import CircularProgress from "material-ui/CircularProgress";
import { Popper, Paper } from "@mui/material";
import propTypes from "./MaterialPropTypes";

function getPopoverWidth(state) {
  const { anchorEl } = state;
  return anchorEl ? { width: anchorEl.clientWidth } : {};
}

class AutoComplete extends Component {
  static propTypes = {
    /**
     * Location of the anchor for the auto complete.
     */
    anchorOrigin: propTypes.origin,
    /**
     * If true, the auto complete is animated as it is toggled.
     */
    animated: PropTypes.bool,
    /**
     * Override the default animation component used.
     */
    animation: PropTypes.func,
    /**
     * Array of strings or nodes used to populate the list.
     */
    dataSource: PropTypes.array.isRequired,
    /**
     * Config for objects list dataSource.
     *
     * @typedef {Object} dataSourceConfig
     *
     * @property {string} text `dataSource` element key used to find a string to be matched for search
     * and shown as a `TextField` input value after choosing the result.
     * @property {string} value `dataSource` element key used to find a string to be shown in search results.
     */
    dataSourceConfig: PropTypes.object,
    /**
     * Disables focus ripple when true.
     */
    disableFocusRipple: PropTypes.bool,
    /**
     * Override style prop for error.
     */
    errorStyle: PropTypes.object,
    /**
     * The error content to display.
     */
    errorText: PropTypes.node,
    /**
     * Callback function used to filter the auto complete.
     *
     * @param {string} searchText The text to search for within `dataSource`.
     * @param {string} key `dataSource` element, or `text` property on that element if it's not a string.
     * @returns {boolean} `true` indicates the auto complete list will include `key` when the input is `searchText`.
     */
    filter: PropTypes.func,
    /**
     * The content to use for adding floating label element.
     */
    floatingLabelText: PropTypes.node,
    /**
     * If true, the field receives the property `width: 100%`.
     */
    fullWidth: PropTypes.bool,
    /**
     * The hint content to display.
     */
    hintText: PropTypes.node,
    /**
     * Override style for list.
     */
    listStyle: PropTypes.object,
    /**
     * The max number of search results to be shown.
     * By default it shows all the items which matches filter.
     */
    maxSearchResults: PropTypes.number,
    /**
     * Delay for closing time of the menu.
     */
    menuCloseDelay: PropTypes.number,
    /**
     * Props to be passed to menu.
     */
    menuProps: PropTypes.object,
    /**
     * Override style for menu.
     */
    menuStyle: PropTypes.object,
    /**
     * Shows loading indicator while data are fetched.
     */
    loading: PropTypes.bool,
    /** @ignore */
    onBlur: PropTypes.func,
    /**
     * Callback function fired when the menu is closed.
     */
    onClose: PropTypes.func,
    /** @ignore */
    onFocus: PropTypes.func,
    /** @ignore */
    onKeyDown: PropTypes.func,
    /**
     * Callback function that is fired when a list item is selected, or enter is pressed in the `TextField`.
     *
     * @param {string} chosenRequest Either the `TextField` input value, if enter is pressed in the `TextField`,
     * or the text value of the corresponding list item that was selected.
     * @param {number} index The index in `dataSource` of the list item selected, or `-1` if enter is pressed in the
     * `TextField`.
     */
    onNewRequest: PropTypes.func,
    /**
     * Callback function that is fired when the user updates the `TextField`.
     *
     * @param {string} searchText The auto-complete's `searchText` value.
     * @param {array} dataSource The auto-complete's `dataSource` array.
     * @param {object} params Additional information linked the update.
     */
    onUpdateInput: PropTypes.func,
    /**
     * Auto complete menu is open if true.
     */
    open: PropTypes.bool,
    /**
     * If true, the list item is showed when a focus event triggers.
     */
    openOnFocus: PropTypes.bool,
    /**
     * Props to be passed to popover.
     */
    popoverProps: PropTypes.object,
    /**
     * Text being input to auto complete.
     */
    searchText: PropTypes.string,
    /**
     * Override the inline-styles of the root element.
     */
    style: PropTypes.object,
    /**
     * Origin for location of target.
     */
    targetOrigin: propTypes.origin,
    /**
     * Override the inline-styles of AutoComplete's TextField element.
     */
    textFieldStyle: PropTypes.object,

    inputProps: PropTypes.object,
  };

  static defaultProps = {
    anchorOrigin: {
      vertical: "bottom",
      horizontal: "left",
    },
    animated: true,
    dataSourceConfig: {
      text: "text",
      value: "value",
    },
    disableFocusRipple: true,
    filter: (searchText, key) =>
      searchText !== "" && key.indexOf(searchText) !== -1,
    fullWidth: false,
    open: false,
    openOnFocus: false,
    onUpdateInput: () => {},
    onNewRequest: () => {},
    menuCloseDelay: 300,
    loading: false,
    targetOrigin: {
      vertical: "top",
      horizontal: "left",
    },
  };

  static contextTypes = {
    muiTheme: PropTypes.object.isRequired,
  };

  state = {
    anchorEl: null,
    focusTextField: true,
    open: false,
    searchText: undefined,
  };

  componentWillMount() {
    this.requestsList = [];
    this.setState({
      open: this.props.open,
      searchText: this.props.searchText || "",
    });
    this.timerTouchTapCloseId = null;
  }

  componentWillReceiveProps(nextProps) {
    if (this.props.searchText !== nextProps.searchText) {
      this.setState({
        searchText: nextProps.searchText,
      });
    }
    if (this.props.open !== nextProps.open) {
      this.setState({
        open: nextProps.open,
        anchorEl: ReactDOM.findDOMNode(this.refs.searchTextField),
      });
    }
  }

  componentWillUnmount() {
    clearTimeout(this.timerTouchTapCloseId);
    clearTimeout(this.timerBlurClose);
  }

  close() {
    this.setState({
      open: false,
      anchorEl: null,
    });

    if (this.props.onClose) {
      this.props.onClose();
    }
  }

  handleRequestClose = () => {
    // Only take into account the Popover clickAway when we are
    // not focusing the TextField.
    if (!this.state.focusTextField) {
      this.close();
    }
  };

  handleMouseEnter = () => {
    this.setState({ isMouseOverMenu: true });
  };

  handleMouseLeave = () => {
    this.setState({ isMouseOverMenu: false });
  };

  handleMouseDown = (event) => {
    // Keep the TextField focused
    event.preventDefault();
  };

  handleItemTouchTap = (event, child) => {
    const dataSource = this.props.dataSource;
    const index = parseInt(child.key, 10);
    const chosenRequest = dataSource[index];
    const searchText = this.chosenRequestText(chosenRequest);

    this._itemJustSelected = true;

    const updateInput = () =>
      this.props.onUpdateInput(searchText, this.props.dataSource, {
        source: "touchTap",
      });
    this.timerTouchTapCloseId = () =>
      setTimeout(() => {
        this.timerTouchTapCloseId = null;
        this._itemJustSelected = false;
        this.close();
        this.props.onNewRequest(chosenRequest, index, event);
      }, this.props.menuCloseDelay);

    if (typeof this.props.searchText !== "undefined") {
      updateInput();
      this.timerTouchTapCloseId();
    } else {
      this.setState(
        {
          searchText: searchText,
        },
        () => {
          updateInput();
          this.timerTouchTapCloseId();
        },
      );
    }
  };

  chosenRequestText = (chosenRequest) => {
    if (typeof chosenRequest === "string") {
      return chosenRequest;
    } else {
      return chosenRequest[this.props.dataSourceConfig.text];
    }
  };

  handleEscKeyDown = () => {
    this.close();
  };

  handleKeyDown = (event) => {
    if (this.props.onKeyDown) this.props.onKeyDown(event);

    switch (keycode(event)) {
      case "enter":
        this.close();
        const searchText = this.state.searchText;
        if (searchText !== "") {
          this.props.onNewRequest(searchText, -1, event);
        }
        break;

      case "esc":
        this.close();
        break;

      case "down":
        event.preventDefault();
        this.setState({
          open: true,
          focusTextField: false,
          anchorEl: ReactDOM.findDOMNode(this.refs.searchTextField),
        });
        break;

      default:
        break;
    }
  };

  handleChange = (event) => {
    const searchText = event.target.value;

    // Make sure that we have a new searchText.
    // Fix an issue with a Cordova Webview
    if (searchText === this.state.searchText) {
      return;
    }

    this.setState(
      {
        searchText: searchText,
        open: true,
        anchorEl: ReactDOM.findDOMNode(this.refs.searchTextField),
      },
      () => {
        this.props.onUpdateInput(searchText, this.props.dataSource, {
          source: "change",
        });
      },
    );
  };

  handleBlur = (event) => {
    if (
      this.state.focusTextField &&
      !this.state.isMouseOverMenu &&
      this.timerTouchTapCloseId === null
    ) {
      this.timerBlurClose = setTimeout(() => {
        this.close();
      }, 0);
    }

    // Don't propagate blur if mouse is over menu (user is selecting an item)
    // or if item was just selected. This prevents redux-form from resetting value.
    if (this._itemJustSelected || this.state.isMouseOverMenu) {
      return;
    }

    if (this.props.onBlur) {
      this.props.onBlur(event);
    }
  };

  handleFocus = (event) => {
    clearTimeout(this.timerBlurClose);

    if (!this.state.open && this.props.openOnFocus) {
      this.setState({
        open: true,
        anchorEl: ReactDOM.findDOMNode(this.refs.searchTextField),
      });
    }

    this.setState({
      focusTextField: true,
    });

    if (this.props.onFocus) {
      this.props.onFocus(event);
    }
  };

  blur() {
    this.refs.searchTextField.blur();
  }

  focus() {
    this.refs.searchTextField.focus();
  }

  render() {
    const {
      anchorOrigin,
      animated,
      animation,
      dataSource,
      dataSourceConfig, // eslint-disable-line no-unused-vars
      disableFocusRipple,
      errorStyle, // eslint-disable-line no-unused-vars
      errorText,
      floatingLabelText,
      inputProps,
      filter,
      fullWidth,
      style,
      hintText,
      maxSearchResults,
      menuCloseDelay, // eslint-disable-line no-unused-vars
      textFieldStyle, // eslint-disable-line no-unused-vars
      menuStyle,
      menuProps,
      listStyle,
      loading,
      targetOrigin,
      onBlur, // eslint-disable-line no-unused-vars
      onClose, // eslint-disable-line no-unused-vars
      onFocus, // eslint-disable-line no-unused-vars
      onKeyDown, // eslint-disable-line no-unused-vars
      onNewRequest, // eslint-disable-line no-unused-vars
      onUpdateInput, // eslint-disable-line no-unused-vars
      openOnFocus, // eslint-disable-line no-unused-vars
      popoverProps,
      searchText: searchTextProp, // eslint-disable-line no-unused-vars
      ...other
    } = this.props;

    const { style: popoverStyle, ...popoverOther } = popoverProps || {};

    const { open, anchorEl, searchText, focusTextField } = this.state;

    const { prepareStyles } = this.context.muiTheme;
    const popoverWidth = getPopoverWidth(this.state);

    const requestsList = [];

    dataSource.every((item, index) => {
      switch (typeof item) {
        case "string":
          if (filter(searchText, item, item)) {
            requestsList.push({
              text: item,
              value: (
                <MenuItem
                  innerDivStyle={{ overflow: "hidden" }}
                  value={item}
                  primaryText={item}
                  disableFocusRipple={disableFocusRipple}
                  key={index}
                />
              ),
            });
          }
          break;

        case "object":
          if (
            item &&
            typeof item[this.props.dataSourceConfig.text] === "string"
          ) {
            const itemText = item[this.props.dataSourceConfig.text];
            if (!this.props.filter(searchText, itemText, item)) break;

            const itemValue = item[this.props.dataSourceConfig.value];
            if (
              itemValue.type &&
              (itemValue.type.muiName === MenuItem.muiName ||
                itemValue.type.muiName === Divider.muiName)
            ) {
              requestsList.push({
                text: itemText,
                value: React.cloneElement(itemValue, {
                  key: index,
                  disableFocusRipple: disableFocusRipple,
                }),
              });
            } else {
              requestsList.push({
                text: itemText,
                value: (
                  <MenuItem
                    innerDivStyle={{ overflow: "hidden" }}
                    primaryText={itemText}
                    disableFocusRipple={disableFocusRipple}
                    key={index}
                  />
                ),
              });
            }
          }
          break;

        default:
        // Do nothing
      }

      return !(
        maxSearchResults &&
        maxSearchResults > 0 &&
        requestsList.length === maxSearchResults
      );
    });

    this.requestsList = requestsList;

    const loadingMenu = open && loading && (
      <div
        onMouseEnter={this.handleMouseEnter}
        onMouseLeave={this.handleMouseLeave}
        onBlur={this.handleBlur}
      >
        <div
          className="croField__menuLoading"
          style={{ width: (anchorEl && anchorEl.clientWidth) || "100%" }}
        >
          <CircularProgress size={20} thickness={2.5} color="#2b6aa5" />
          <span>Načítám data...</span>
        </div>
      </div>
    );

    const menu = open && !loading && requestsList.length > 0 && (
      <div
        onMouseEnter={this.handleMouseEnter}
        onMouseLeave={this.handleMouseLeave}
        onBlur={this.handleBlur}
      >
        <Menu
          ref="menu"
          autoWidth={false}
          disableAutoFocus={focusTextField}
          onEscKeyDown={this.handleEscKeyDown}
          initiallyKeyboardFocused={true}
          onItemTouchTap={this.handleItemTouchTap}
          onMouseDown={this.handleMouseDown}
          className="croField__menu"
          style={menuStyle}
          listStyle={listStyle}
          {...menuProps}
        >
          {requestsList.map((i) => i.value)}
        </Menu>
      </div>
    );

    return (
      <div className={`croField__autoComplete${fullWidth ? " croField__autoComplete--fullWidth" : ""}`} style={prepareStyles(style || {})}>
        <div className={`croField${errorText ? " croField--error" : ""}`}>
          <input
            ref="searchTextField"
            className="croField__input"
            autoComplete="off"
            onBlur={this.handleBlur}
            onFocus={this.handleFocus}
            onKeyDown={this.handleKeyDown}
            placeholder={hintText || floatingLabelText}
            aria-label={hintText || floatingLabelText}
            {...other}
            value={searchText}
            onChange={this.handleChange}
            aria-label={inputProps ? inputProps['aria-label'] : 'Hledat'}
          />
          {errorText && <span className="croField__errorText">{errorText}</span>}
        </div>
        <Popper
          open={open}
          anchorEl={anchorEl}
          placement="bottom-start"
          style={{
            zIndex: 1300,
            ...popoverWidth,
            ...popoverStyle,
          }}
        >
          <Paper elevation={3} style={{ overflowX: "hidden", overflowY: "auto", maxHeight: 500 }}>{loadingMenu || menu}</Paper>
        </Popper>
      </div>
    );
  }
}

AutoComplete.levenshteinDistance = (searchText, key) => {
  const current = [];
  let prev;
  let value;

  for (let i = 0; i <= key.length; i++) {
    for (let j = 0; j <= searchText.length; j++) {
      if (i && j) {
        if (searchText.charAt(j - 1) === key.charAt(i - 1)) value = prev;
        else value = Math.min(current[j], current[j - 1], prev) + 1;
      } else {
        value = i + j;
      }
      prev = current[j];
      current[j] = value;
    }
  }
  return current.pop();
};

AutoComplete.noFilter = () => true;

AutoComplete.defaultFilter = AutoComplete.caseSensitiveFilter = (
  searchText,
  key,
) => {
  return searchText !== "" && key.indexOf(searchText) !== -1;
};

AutoComplete.caseInsensitiveFilter = (searchText, key) => {
  return key.toLowerCase().indexOf(searchText.toLowerCase()) !== -1;
};

AutoComplete.levenshteinDistanceFilter = (distanceLessThan) => {
  if (distanceLessThan === undefined) {
    return AutoComplete.levenshteinDistance;
  } else if (typeof distanceLessThan !== "number") {
    throw Error(
      "Error: AutoComplete.levenshteinDistanceFilter is a filter generator, not a filter!",
    );
  }

  return (s, k) => AutoComplete.levenshteinDistance(s, k) < distanceLessThan;
};

AutoComplete.fuzzyFilter = (searchText, key) => {
  const compareString = key.toLowerCase();
  searchText = searchText.toLowerCase();

  let searchTextIndex = 0;
  for (let index = 0; index < key.length; index++) {
    if (compareString[index] === searchText[searchTextIndex]) {
      searchTextIndex += 1;
    }
  }

  return searchTextIndex === searchText.length;
};

AutoComplete.Item = MenuItem;
AutoComplete.Divider = Divider;

export default AutoComplete;
